import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import ReactEcharts from 'echarts-for-react';
import merge from 'lodash.merge';

import { Div, Text } from '@pinpoint/design-kit';

import Wrapper from 'src/Wrapper';
import { stylePropType } from 'src/PropTypes';
import { Style1 } from 'src/styles';
import { formatNumber } from 'src/functions';
import { EventEmitter } from 'src/components';

/* eslint-disable no-bitwise */
/* eslint-disable no-mixed-operators */

const uuidv4 = () =>
  ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
  );

/* eslint-enable no-mixed-operators */
/* eslint-ensable no-bitwise */

const TOTAL_COLORS = 22;

// MULTIPLIER indicates the difference in order between the
// probabilities and the think alike score. Historically,
// thinkalike scores were in the range 1-1000 and probability
// was between 0-1. Now with the multipler set to 1, the charting
// tool expects the thinkalike score and probaboility in the
// same range.
const MULTIPLIER = 1000;

// Number of floating points resolution should be retained from
// the left and right intervals of the distribution
const INTERVAL_RESOLUTION = 6;
// Number of floating point of thinkalike score for visualization
const DISPLAY_RESOLUTION = 0;

const eventEmitter = new EventEmitter();

class DuplicateResistantQueue {
  constructor() {
    this.queue = [];
    this.TIMEOUT = 20;
  }

  dispatch = ({ start, end, startingLift, endingLift }) => {
    const theItem = this.queue.find(
      (item) =>
        (item.start === start && item.end === end) ||
        (item.startingLift === startingLift && item.endingLift === endingLift)
    );
    if (!theItem) {
      this.queue.push({
        start,
        end,
        startingLift,
        endingLift,
        reaped: false,
        id: uuidv4(),
      });
      return true;
    }
    const { reaped, id } = theItem;
    if (!reaped) {
      setTimeout(
        (id_) => {
          const index = this.queue.findIndex((item) => item.id === id_);
          this.queue.splice(index, 1);
        },
        this.TIMEOUT,
        id
      );
      theItem.reaped = true;
    }
    return false;
  };
}

/* eslint-disable react/prop-types */

function StatusBar({ dataModelType }) {
  const [users, setUsers] = useState();
  const [usersPercentage, setUsersPercentage] = useState();
  const [averageLift, setAverageLift] = useState();
  const [averageTalScore, setAverageTalScore] = useState();
  const avgScoreFormat = useMemo(() => {
    if (!averageTalScore) return '-';
    switch (dataModelType) {
      case 'ACT_FREQUENCY':
        return `${averageTalScore.toFixed(7)}`;
      case 'ACT_SEVERITY':
        return `$${formatNumber(Math.round(averageTalScore))}`;
      case 'ACT_LOSS_MULT':
        return `$${formatNumber(Math.round(averageTalScore))}`;
      case 'ACT_LOSS_DIRECT':
        return `$${formatNumber(Math.round(averageTalScore))}`;
      default:
        return averageTalScore.toFixed(2);
    }
  }, [averageTalScore]);
  const avgSuffix = useMemo(() => {
    let headerName = '';
    switch (dataModelType) {
      case 'ACT_FREQUENCY':
        headerName = ' Frequency';
        return headerName;
      case 'ACT_SEVERITY':
        headerName = ' Severity';
        return headerName;
      case 'ACT_LOSS_MULT':
        headerName = ' Loss Cost Mult';
        return headerName;
      case 'ACT_LOSS_DIRECT':
        headerName = ' Loss Cost Direct';
        return headerName;
      default:
        return ' Score';
    }
  }, [dataModelType]);
  const liftSuffix = useMemo(() => (dataModelType === 'CLASSIFIER' ? 'Lift' : 'Loss Ratio Lift'), [dataModelType]);

  eventEmitter.subscribe('users', setUsers);
  eventEmitter.subscribe('usersPercentage', setUsersPercentage);
  eventEmitter.subscribe('averageLift', setAverageLift);
  eventEmitter.subscribe('averageTalScore', setAverageTalScore);

  return (
    <Div d="flex" justify="space-around" align="center" h="2.5rem" m={{ t: '1rem' }}>
      <Div
        data-test="statusbar-content"
        d="flex"
        textColor="white"
        p={{ x: '1.25rem', y: '0.75rem' }}
        rounded="sm"
        style={{
          backgroundColor: 'rgba(50, 50, 50, 0.7)',
        }}
      >
        <Text textSize="subtitle">Users: &nbsp;</Text>
        <Text textSize="subtitle">
          {users ? formatNumber(users) : '-'}
          &nbsp; &nbsp; | &nbsp; &nbsp;
        </Text>
        <Text textSize="subtitle">% of Users: &nbsp;</Text>
        <Text textSize="subtitle">
          {usersPercentage ? `${usersPercentage.toFixed(1)}%` : '-'}
          &nbsp; &nbsp; | &nbsp; &nbsp;
        </Text>
        <Text textSize="subtitle">Average Predicted {liftSuffix}: &nbsp;</Text>
        <Text textSize="subtitle">
          {averageLift ? averageLift.toFixed(2) : '-'}
          &nbsp; &nbsp; | &nbsp; &nbsp;
        </Text>
        <Text textSize="subtitle">Average Predicted{avgSuffix}: &nbsp;</Text>
        <Text textSize="subtitle">{avgScoreFormat}</Text>
      </Div>
    </Div>
  );
}

/* eslint-enable */

function Histogram({
  title,
  intervals,
  countSeries,
  liftSeries,
  customMultiplier,
  style,
  options,
  onChange,
  gradient: gradientProp,
  onGradientChange,
  dataModelType,
}) {
  const [gradient, setGradient] = useState(gradientProp);
  const eventsQueueRef = useRef();
  const chartRef = useRef();
  const [firstRender, setFirstRender] = useState(true);
  const updateChartRef = (el) => {
    chartRef.current = el?.getEchartsInstance?.() ?? null;
  };

  const liftValues = liftSeries.values;
  const belowOnes = liftValues.filter((value) => value < 1);
  const max = Math.max(...liftValues);
  const minBelowOne = Math.min(...belowOnes);
  const belowOneRange = 1 - minBelowOne;
  const totalRange = max - minBelowOne;
  const belowOneTotalColors = (belowOneRange * TOTAL_COLORS) / totalRange - 1;
  const aboveOneTotalColors = TOTAL_COLORS - belowOneTotalColors;

  const visualMapColors = Style1.COLORS.generateGradient(belowOneTotalColors, aboveOneTotalColors, gradient);

  const totalCustomers = useMemo(() => countSeries.values.reduce((a, b) => a + b, 0), [countSeries]);

  const dataset = useMemo(
    () =>
      intervals.map(({ left, right }, countIdx) => {
        // NOTE: find the current lift in this interval
        let minInterval = liftSeries.minInterval[0] * (customMultiplier || MULTIPLIER);
        let liftIndex = 0;
        for (let i = 1; i < liftSeries.minInterval.length; i += 1) {
          minInterval = liftSeries.minInterval[i] * (customMultiplier || MULTIPLIER);
          if (right < minInterval) break;
          liftIndex = i;
        }

        return [
          right.toFixed(INTERVAL_RESOLUTION),
          countSeries.values[countIdx],
          liftSeries.values[liftIndex],
          left.toFixed(INTERVAL_RESOLUTION),
        ];
      }),
    [liftSeries, intervals]
  );

  /**
   * dispatchChange - recalculate the summary/status bar values
   * @param startIndex - the fist entry in the dataset to count
   * @param endIndex - the last entry in the dataset to count
   */
  const updateStatusBarValues = useCallback(
    (startIndex, endIndex, startingLift, endingLift) => {
      const { users, liftSum, scoreSum } = dataset.slice(startIndex, endIndex + 1).reduce(
        (acc, cur) => {
          if (cur[2] <= endingLift && cur[2] >= startingLift) {
            acc.scoreSum += parseFloat(cur[0]) * cur[1];
            acc.users += cur[1];
            acc.liftSum += cur[2] * cur[1];
          }
          return acc;
        },
        { users: 0, liftSum: 0, scoreSum: 0 }
      );
      const usersPercentage = (users / totalCustomers) * 100;
      // NOTE: This is a hack, if the whole distribution is selected bump the average to lift to 1
      const liftAverage = startIndex === 0 && endIndex === dataset.length - 1 ? 1 : liftSum / users;
      const thinkalikeScoreAverage = scoreSum / users;
      eventEmitter.dispatch('users', users);
      eventEmitter.dispatch('usersPercentage', usersPercentage);
      eventEmitter.dispatch('averageLift', liftAverage);
      eventEmitter.dispatch('averageTalScore', thinkalikeScoreAverage);
      onChange(users, usersPercentage, liftAverage, thinkalikeScoreAverage);
    },
    [dataset, onChange, totalCustomers, eventEmitter]
  );

  const chartOption = {
    title: {
      text: title,
      ...style.global.title,
      ...style.Histogram.title,
    },
    grid: {
      ...style.global.grid,
      ...style.Histogram.grid,
    },
    textStyle: {
      ...style.Histogram.textStyle,
    },
    label: {
      ...style.Histogram.label,
    },
    backgroundColor: style.global.backgroundColor,
    toolbox: {
      ...style.global.toolbox,
    },
    tooltip: {
      formatter: ({ data }) =>
        data &&
        `
      <div style='padding: .75rem 1rem;line-height: 1.4;font-size:1rem'>
        Number of Customers: ${data[1].toFixed(0)}
        <br />
        Percentage of Customers: ${((data[1] * 100.0) / totalCustomers).toFixed(2)}%
        <br />
        Lift: ${data[2] < 1 ? ((-1 / data[2]) * 100).toFixed(0) : (data[2] * 100).toFixed(0)}%
        <br />
        Thinkalike score: ${parseFloat(data[3]).toFixed(DISPLAY_RESOLUTION)} - ${parseFloat(data[0]).toFixed(
          DISPLAY_RESOLUTION
        )}
      </div>
      `,
    },
    xAxis: {
      ...style.Histogram.xAxis,
      axisLabel: {
        ...style.Histogram.xAxis.axisLabel,
        formatter: (x) => parseFloat(x).toFixed(DISPLAY_RESOLUTION),
      },
    },
    yAxis: {
      ...style.Histogram.yAxis,
    },
    dataset: {
      source: dataset,
      sourceHeader: false,
    },
    visualMap: {
      ...style.Histogram.visualMap,
      min: Math.min(...liftSeries.values),
      max: Math.max(...liftSeries.values),
      seriesIndex: 0,
      dimension: 2,
      inRange: {
        color: visualMapColors,
      },
    },
    dataZoom: {
      ...style.Histogram.dataZoom,
    },
    series: [
      {
        type: 'bar',
        name: countSeries.name,
        encode: {
          x: 0,
          y: 1,
        },
        ...style.Histogram.series,
      },
    ],
  };
  const mergedOptions = merge(chartOption, options);

  // NOTE: on mount, create the event queue
  useEffect(() => {
    eventsQueueRef.current = new DuplicateResistantQueue();
  }, []);

  // NOTE: on new dataset init the
  useEffect(() => {
    if (firstRender && dataset && intervals) {
      let startingLift = 1000;
      let endingLift = -1;
      for (let i = 0; i <= dataset.length - 1; i += 1) {
        startingLift = dataset[i][2] < startingLift ? dataset[i][2] : startingLift;
        endingLift = dataset[i][2] > endingLift ? dataset[i][2] : endingLift;
      }
      updateStatusBarValues(0, intervals.length - 1, startingLift, endingLift);
      setFirstRender(false);
    }
  }, [dataset, intervals, firstRender]);

  useEffect(() => {
    setGradient(gradientProp);
  }, [gradientProp]);

  return (
    <Wrapper>
      <ReactEcharts
        notMerge
        option={mergedOptions}
        theme="pinpoint"
        onEvents={{
          datazoom: ({ start: intervalStart, end: intervalEnd }) => {
            if (Number.isFinite(intervalStart) && Number.isFinite(intervalEnd)) {
              const startIndex = Math.floor((intervalStart * (intervals.length - 1)) / 100);
              const endIndex = Math.ceil((intervalEnd * (intervals.length - 1)) / 100);
              let startingLift = 1000;
              let endingLift = -1;
              for (let i = startIndex; i <= endIndex; i += 1) {
                startingLift = dataset[i][2] < startingLift ? dataset[i][2] : startingLift;
                endingLift = dataset[i][2] > endingLift ? dataset[i][2] : endingLift;
              }
              if (Number.isFinite(startingLift) && Number.isFinite(endingLift)) {
                if (
                  eventsQueueRef?.current?.dispatch?.({
                    start: intervalStart,
                    end: intervalEnd,
                    startingLift,
                    endingLift,
                  })
                ) {
                  updateStatusBarValues(startIndex, endIndex, startingLift, endingLift);
                  chartRef?.current?.dispatchAction?.({
                    type: 'selectDataRange',
                    selected: [startingLift, endingLift],
                  });
                }
              }
            }
          },
          datarangeselected: ({ selected }) => {
            const [startingLift, endingLift] = selected;
            let startIndex = 0;
            let endIndex = intervals.length - 1;

            for (let i = 0; i < dataset.length; i += 1) {
              if (dataset[i][2] >= startingLift && dataset[i][2] <= endingLift) {
                startIndex = i;
                break;
              }
            }

            for (let i = dataset.length - 1; i >= 0; i -= 1) {
              if (dataset[i][2] >= startingLift && dataset[i][2] <= endingLift) {
                endIndex = i;
                break;
              }
            }

            const intervalStart = (parseFloat(startIndex) / (intervals.length - 1)) * 100;
            const intervalEnd = (parseFloat(endIndex) / (intervals.length - 1)) * 100;

            if (Number.isFinite(intervalStart) && Number.isFinite(intervalEnd)) {
              if (
                eventsQueueRef.current.dispatch({
                  start: intervalStart,
                  end: intervalEnd,
                  startingLift,
                  endingLift,
                })
              ) {
                updateStatusBarValues(startIndex, endIndex, startingLift, endingLift);
                chartRef?.current?.dispatchAction?.({
                  type: 'dataZoom',
                  dataZoomIndex: 0,
                  start: intervalStart,
                  end: intervalEnd,
                });
              }
            }
          },
        }}
        ref={updateChartRef}
        style={{
          width: '100%',
          height: 'calc(100% - 6rem)',
        }}
      />
      <StatusBar dataModelType={dataModelType} />
      <Div d="flex" align="center" justify="flex-end" h="2.5rem">
        <Div
          tag="span"
          h="2.5rem"
          w="2.5rem"
          rounded="md"
          border="1px solid"
          borderColor="shadeE6"
          cursor="pointer"
          hoverBorderColor="shadeCC"
          data-tip="Blue-orange gradient"
          style={{
            ...style.COLORS.BLUE_ORANGE_CSS,
          }}
          onClick={() => {
            setGradient(style.COLORS.GRADIENTS.BLUE_ORANGE);
            onGradientChange(style.COLORS.GRADIENTS.BLUE_ORANGE);
          }}
          m={{ l: '0.4rem' }}
        />
        <Div
          tag="span"
          h="2.5rem"
          w="2.5rem"
          rounded="md"
          border="1px solid"
          borderColor="shadeE6"
          cursor="pointer"
          hoverBorderColor="shadeCC"
          data-tip="Blue-red gradient"
          style={{
            ...style.COLORS.ELECTRIC_BLUE_RED_CSS,
          }}
          onClick={() => {
            setGradient(style.COLORS.GRADIENTS.ELECTRIC_BLUE_RED);
            onGradientChange(style.COLORS.GRADIENTS.ELECTRIC_BLUE_RED);
          }}
          m={{ l: '0.4rem' }}
        />
        <Div
          tag="span"
          h="2.5rem"
          w="2.5rem"
          rounded="md"
          border="1px solid"
          borderColor="shadeE6"
          cursor="pointer"
          hoverBorderColor="shadeCC"
          data-tip="Full-spectrum gradient"
          style={{
            ...style.COLORS.MULTICHROMATIC_CSS,
          }}
          onClick={() => {
            setGradient(style.COLORS.GRADIENTS.MULTICHROMA);
            onGradientChange(style.COLORS.GRADIENTS.MULTICHROMA);
          }}
          m={{ l: '0.4rem' }}
        />
      </Div>
    </Wrapper>
  );
}

Histogram.propTypes = {
  title: PropTypes.string,
  intervals: PropTypes.arrayOf(
    PropTypes.shape({
      left: PropTypes.number,
      right: PropTypes.number,
    })
  ).isRequired,
  customMultiplier: PropTypes.number,
  countSeries: PropTypes.shape({
    name: PropTypes.string,
    values: PropTypes.arrayOf(PropTypes.number),
  }).isRequired,
  liftSeries: PropTypes.shape({
    name: PropTypes.string,
    values: PropTypes.arrayOf(PropTypes.number),
    minInterval: PropTypes.arrayOf(PropTypes.number),
  }).isRequired,
  style: stylePropType,
  options: PropTypes.shape({}),
  onChange: PropTypes.func,
  gradient: PropTypes.string,
  onGradientChange: PropTypes.func,
  dataModelType: PropTypes.string,
};

Histogram.defaultProps = {
  title: '',
  style: Style1,
  options: {},
  onChange: () => null,
  gradient: Style1.COLORS.GRADIENTS.BLUE_ORANGE,
  customMultiplier: null,
  onGradientChange: () => null,
  dataModelType: 'CLASSIFIER',
};

export default Histogram;
